import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  combineLatest,
  mergeMap,
  Observable,
  of,
  take,
  tap,
  concatMap,
  takeWhile,
  catchError,
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';

import { ActivateComponentService } from './activate-component.service';
import { ProjectSetup, Subproject, UserSession } from '@shared/models';
import { OfflineFacade, UserDetailsFacade } from '@shared/state/facades';
import { PersistenceService } from './persistence';
import { CryptoService } from './crypto.service';
import { NavigationService, UserRightsService } from '.';
import { DprDetails } from '@shared/models/dpr';
import {
  canDeactivateUnsavedChanges,
  ignoreNextGuardCall$,
} from '@shared/guards';

@UntilDestroy()
@Injectable()
export class SubprojectService {
  constructor(
    private userDetailsFacade: UserDetailsFacade,
    private offlineFacade: OfflineFacade,
    private userRightsService: UserRightsService,
    private persistenceService: PersistenceService,
    private navigationService: NavigationService,
    private location: Location,
    private cryptoService: CryptoService,
    private activateComponentService: ActivateComponentService,
    private dialog: MatDialog
  ) {}

  selectSubproject(
    subprojectId: number,
    userDetails: UserSession,
    redirectToSubprojectRoute = true
  ): Observable<boolean> {
    return this.activateComponentService.activatedComponent.pipe(
      untilDestroyed(this),
      take(1),
      concatMap((activatedComponent) => {
        if (!activatedComponent || !redirectToSubprojectRoute) {
          return of(true);
        }

        return canDeactivateUnsavedChanges(
          activatedComponent,
          this.dialog
        ).pipe(tap((result) => result && ignoreNextGuardCall$.next(true)));
      }),
      takeWhile((result) => result),
      concatMap(() => this.loadSubproject(subprojectId, userDetails)),
      concatMap((res) => {
        if (redirectToSubprojectRoute) {
          return this.redirectToSubprojectRoute(subprojectId, userDetails);
        }
        return of(res);
      }),
      catchError(() => {
        ignoreNextGuardCall$.next(false);
        return of(null);
      })
    );
  }

  updateSubprojects(subprojects: Subproject[]): void {
    this.userDetailsFacade.setSubprojects(subprojects);

    this.userDetailsFacade.query.selectedSubproject$
      .pipe(
        untilDestroyed(this),
        take(1),
        tap((selectedSubproject) => {
          let newSelectedSubprojectDetails = selectedSubproject
            ? subprojects?.find((x) => x.id === selectedSubproject.id)
            : null;
          const projectSetup = newSelectedSubprojectDetails
            ? selectedSubproject.data
            : null;

          if (!newSelectedSubprojectDetails && subprojects?.length) {
            newSelectedSubprojectDetails = subprojects[0];
          }

          if (!subprojects?.length || !newSelectedSubprojectDetails) {
            this.cleanSelectedSubproject();
          } else {
            this.updateSelectedSubprojectDetails(
              newSelectedSubprojectDetails,
              projectSetup
            );
          }
        })
      )
      .subscribe();
  }

  redirectToSubprojectRoute(
    subprojectId: number,
    userDetails: UserSession
  ): Observable<boolean> {
    const encryptedId = this.cryptoService.encryptId(
      subprojectId ?? userDetails?.selectedSubproject.id
    );

    if (!subprojectId) {
      return this.navigationService.navigateToRouteWithReplaceAsync(
        userDetails.selectedClientId,
        'dprs',
        encryptedId
      );
    }

    const redirectState = this.userRightsService.getFirstAllowedSubprojectState(
      this.location.path(),
      subprojectId,
      userDetails
    );

    // TODO(Skip) probably not needed any more, because the user receives Forbidden when trying to access a subproject
    // directly from the route, for which he/she has no rights (the get Project Setup call fails);
    // the user is redirected to the first available route after the Forbidden message is dismissed

    // if (!redirectState) {
    //   // go to first allowed subproject
    //   const firstAllowedSubproject =
    //     this.userRightsService.getFirstAllowedSubproject(userDetails);

    //   if (firstAllowedSubproject) {
    //     this.selectSubproject(firstAllowedSubproject.id, userDetails)
    //       .pipe(untilDestroyed(this))
    //       .subscribe();

    //     return of(false);
    //   }

    //   return this.navigationService.goToNoAccessErrorRouteAsync();
    // }

    // this is needed in order to not complete and change the bypassGuard flag before navigation
    return this.navigationService.navigateToRouteWithReplaceAsync(
      userDetails.selectedClientId,
      redirectState,
      encryptedId,
      this.navigationService.getSubRoute(redirectState, this.location.path())
    );
  }

  //#region private methods

  private loadSubproject(
    subprojectId: number,
    userDetails: UserSession
  ): Observable<boolean> {
    if (this.isSelected(subprojectId, userDetails)) {
      return of(false);
    }

    const subproject = this.getSubprojectFromUserSession(
      subprojectId,
      userDetails
    );

    // The store's selected subproject/project are still the previous ones at this point — these
    // three calls address the one being switched to explicitly, rather than through the header
    // interceptor's default, which would still read the previous subproject for this request.
    // A subproject missing from the session is one the user can't open, such as one reached
    // through a shared link. Its project setup is still requested by its own id, so the API
    // refuses it and the Forbidden handling redirects to the first allowed page. It isn't saved
    // as the last selected one, because that call is refused too and would raise a second dialog.
    const updateSelectedSubprojectObs: Observable<void> = subproject
      ? this.persistenceService.updateSelectedSubproject(subprojectId)
      : of(null);

    const projectSetupObs: Observable<ProjectSetup> =
      this.persistenceService.action.getProjectSetup(subprojectId);

    const lastThreeDprsObs: Observable<DprDetails[]> =
      this.userRightsService.hasRightsForSubprojectState(
        'dprs',
        subprojectId,
        userDetails
      ) && subproject.isReleased
        ? this.persistenceService.action.getLastThreeDprs(subprojectId)
        : of(null);

    return combineLatest([
      updateSelectedSubprojectObs,
      projectSetupObs,
      lastThreeDprsObs,
    ]).pipe(
      mergeMap(([_updSubproj, projectSetup, lastThreeDprs]) => {
        this.setState(subprojectId, projectSetup, lastThreeDprs, userDetails);
        return of(true);
      }),
      // Loading a subproject is a one-shot read, but offline the project setup
      // and the last three DPRs come from store selectors that never complete.
      // Since setState writes back to those same selectors, anything past the
      // first emission would feed itself.
      take(1)
    );
  }

  private isSelected(subprojectId: number, userDetails: UserSession): boolean {
    const selectedSubproject = userDetails.selectedSubproject;
    return (
      selectedSubproject &&
      selectedSubproject.data &&
      selectedSubproject.id === subprojectId
    );
  }

  private setState(
    subprojectId: number,
    projectSetup: ProjectSetup,
    lastThreeDprs: DprDetails[],
    userDetails: UserSession
  ): void {
    this.offlineFacade.clearDprDetails();

    const subproject = this.getSubprojectFromUserSession(
      subprojectId,
      userDetails
    );
    this.userDetailsFacade.setSelectedSubprojectDetails(
      subproject,
      projectSetup
    );

    this.offlineFacade.setDprDetailsList(lastThreeDprs);
  }

  private getSubprojectFromUserSession(
    subprojectId: number,
    userDetails: UserSession
  ): Subproject {
    return userDetails.subprojects.find((x) => x.id === subprojectId);
  }

  private cleanSelectedSubproject(): void {
    this.userDetailsFacade.setSelectedSubproject(null);
  }

  private updateSelectedSubprojectDetails(
    subprojectDetails: Subproject,
    projectSetup: ProjectSetup
  ): void {
    if (!subprojectDetails) {
      return;
    }

    this.userDetailsFacade.setSelectedSubproject(subprojectDetails);
    this.userDetailsFacade.setProjectSetup(projectSetup);
  }

  //#endregion
}
